import { useEffect, useState } from 'react';
import { useUnit } from 'state/unit';
import { AudioPlayer } from 'utils/audio';
import { DrawableAudioPlayer } from 'utils/audio/types';
import { millisToSec } from 'utils/time';
import usePreviousRef from './usePreviousRef';

export type UseUnitAudioPlayerCreatorOptions = {
  extendedPlayback?: boolean;
};

export default function useUnitAudioPlayerCreator(
  element: HTMLAudioElement | null,
  opts?: UseUnitAudioPlayerCreatorOptions,
): DrawableAudioPlayer | undefined {
  const { extendedPlayback } = opts ?? {};

  const unitRef = usePreviousRef(useUnit());
  const extendPlaybackRef = usePreviousRef(extendedPlayback);

  const [player, setPlayer] = useState<DrawableAudioPlayer | undefined>(
    undefined,
  );

  // hook should only run when element changes
  useEffect(() => {
    if (element && unitRef.current) {
      setPlayer(
        new AudioPlayer(element, {
          startSec: millisToSec(unitRef.current.startMillis),
          endSec: extendPlaybackRef.current
            ? Infinity
            : millisToSec(
                unitRef.current.startMillis + unitRef.current.durationMillis,
              ),
        }),
      );
    }

    return () => {
      // when the audio element unmounts, this will be called with null.  audio
      // should be unloaded otherwise some browsers can slow down/hang (Chrome)
      if (element) {
        element.src = '';
        element.load();
      }
    };
  }, [element, extendPlaybackRef, unitRef]);

  return player;
}
